import { useState, useEffect, useRef } from 'react';
import styles from './FiltrosClientes.module.css';

const FiltrosClientes = ({
    onFilterChange,
    onSearch,
    onSearchChange,
    onExportCSV,
    isExporting = false,
    activeTab,
    cidadesDisponiveis = [],
    estadosDisponiveis = [],
    initialSearch = '',
    initialCidades = [],
    initialEmail = '',
    initialPedidosRange = '',
    initialDataInicio = '',
    initialDataFim = '',
    // Props para uso legado (clientes inativos)
    initialCidade = '',
    initialEstado = '',
    initialCpf = '',
    initialValorMin = '',
    initialValorMax = '',
    initialOrdenacao = 'dias_sem_comprar',
    initialPageSize = 100,
    initialDiasInativos = 60,
    onOrdenacaoChange,
    onPageSizeChange,
    onDiasInativosChange,
    onAtualizar,
    loading = false
}) => {
    // Estados para Gestão de Clientes
    const [searchTerm, setSearchTerm] = useState(initialSearch);
    const [cidadesSelecionadas, setCidadesSelecionadas] = useState(initialCidades);
    const [cidadeDropdownOpen, setCidadeDropdownOpen] = useState(false);
    const [cidadeSearch, setCidadeSearch] = useState('');
    const cidadeDropdownRef = useRef(null);

    // Estados para modo legado (clientes inativos)
    const [cidadeSelecionada, setCidadeSelecionada] = useState(initialCidade);
    const [estadoSelecionado, setEstadoSelecionado] = useState(initialEstado);
    const [cpfBusca, setCpfBusca] = useState(initialCpf);
    const [estadoDropdownOpen, setEstadoDropdownOpen] = useState(false);
    const estadoDropdownRef = useRef(null);
    const [valorMin, setValorMin] = useState(initialValorMin);
    const [valorMax, setValorMax] = useState(initialValorMax);
    const [ordenacao, setOrdenacao] = useState(initialOrdenacao);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [diasInativos, setDiasInativos] = useState(initialDiasInativos);

    // Determinar se estamos no modo Gestão de Clientes ou modo Legado
    const isGestaoMode = typeof onSearch === 'function';

    // Ref para evitar que o debounce dispare na montagem inicial (resetando filtro do pai para '')
    const isFirstRender = useRef(true);
    // Ref para debounce manual da busca (evita dependência de useEffect)
    const searchDebounceRef = useRef(null);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (cidadeDropdownRef.current && !cidadeDropdownRef.current.contains(event.target)) {
                setCidadeDropdownOpen(false);
                setCidadeSearch('');
            }
            if (estadoDropdownRef.current && !estadoDropdownRef.current.contains(event.target)) {
                setEstadoDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Sincronizar estados quando props mudarem
    // NÃO sincronizar searchTerm via initialSearch no modo legado — cria loop bidirecional
    useEffect(() => { setCidadesSelecionadas(initialCidades); }, [initialCidades]);
    useEffect(() => { setCidadeSelecionada(initialCidade); }, [initialCidade]);
    useEffect(() => { setEstadoSelecionado(initialEstado); }, [initialEstado]);
    useEffect(() => { setCpfBusca(initialCpf); }, [initialCpf]);
    useEffect(() => { setValorMin(initialValorMin); }, [initialValorMin]);
    useEffect(() => { setValorMax(initialValorMax); }, [initialValorMax]);
    useEffect(() => { setOrdenacao(initialOrdenacao); }, [initialOrdenacao]);
    useEffect(() => { setPageSize(initialPageSize); }, [initialPageSize]);
    useEffect(() => { setDiasInativos(initialDiasInativos); }, [initialDiasInativos]);

    useEffect(() => {
        // Pular primeiro render para não resetar o filtro do pai com string vazia na montagem
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const timer = setTimeout(() => {
            if (isGestaoMode && onSearch) {
                onSearch(searchTerm);
            } else if (!isGestaoMode && onSearchChange) {
                // Usar onSearchChange (referência estável do React: setFiltro) diretamente
                onSearchChange(searchTerm);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm, onSearchChange, isGestaoMode, onSearch]);

    // Handler para toggle de cidade selecionada (multi-select)
    const handleCidadeToggle = (cidade) => {
        const newCidades = cidadesSelecionadas.includes(cidade)
            ? cidadesSelecionadas.filter(c => c !== cidade)
            : [...cidadesSelecionadas, cidade];

        setCidadesSelecionadas(newCidades);
        if (onFilterChange) {
            onFilterChange({ cidades: newCidades });
        }
    };

    // Handler para limpar todas as cidades
    const handleLimparCidades = () => {
        setCidadesSelecionadas([]);
        if (onFilterChange) {
            onFilterChange({ cidades: [] });
        }
        setCidadeDropdownOpen(false);
    };

    // Handler para selecionar todas as cidades filtradas
    const handleSelecionarTodas = () => {
        const cidadesFiltradas = cidadesDisponiveis.filter(cidade =>
            cidade.toLowerCase().includes(cidadeSearch.toLowerCase())
        );

        // Adicionar apenas as que ainda não estão selecionadas
        const newCidades = [...new Set([...cidadesSelecionadas, ...cidadesFiltradas])];
        setCidadesSelecionadas(newCidades);
        if (onFilterChange) {
            onFilterChange({ cidades: newCidades });
        }
    };

    // Filtrar cidades pelo termo de busca
    const cidadesFiltradas = cidadesDisponiveis.filter(cidade =>
        cidade.toLowerCase().includes(cidadeSearch.toLowerCase())
    );

    // Handlers para modo legado
    const handleCidadeChange = (cidade) => {
        setCidadeSelecionada(cidade);
        setCidadeDropdownOpen(false);
        if (onFilterChange) {
            onFilterChange({ cidade });
        }
    };

    const handleEstadoChange = (estado) => {
        setEstadoSelecionado(estado);
        setEstadoDropdownOpen(false);
        if (onFilterChange) {
            onFilterChange({ estado });
        }
    };

    // Formatar CPF/CNPJ enquanto digita
    const formatarCpfCnpj = (valor) => {
        const numeros = valor.replace(/\D/g, '');
        if (numeros.length <= 11) {
            // CPF: 000.000.000-00
            return numeros
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d{1,2})/, '$1-$2')
                .replace(/(-\d{2})\d+?$/, '$1');
        } else {
            // CNPJ: 00.000.000/0000-00
            return numeros
                .replace(/(\d{2})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1/$2')
                .replace(/(\d{4})(\d)/, '$1-$2')
                .replace(/(-\d{2})\d+?$/, '$1');
        }
    };

    const handleCpfChange = (e) => {
        const valorFormatado = formatarCpfCnpj(e.target.value);
        setCpfBusca(valorFormatado);
        if (onFilterChange) {
            onFilterChange({ cpf: valorFormatado });
        }
    };

    const handleValorMinChange = (e) => {
        const valor = e.target.value;
        setValorMin(valor);
        if (onFilterChange) {
            onFilterChange({ valorMin: valor });
        }
    };

    const handleValorMaxChange = (e) => {
        const valor = e.target.value;
        setValorMax(valor);
        if (onFilterChange) {
            onFilterChange({ valorMax: valor });
        }
    };

    const handleOrdenacaoChange = (e) => {
        const valor = e.target.value;
        setOrdenacao(valor);
        if (onOrdenacaoChange) onOrdenacaoChange(valor);
    };

    const handlePageSizeChange = (e) => {
        const valor = parseInt(e.target.value);
        setPageSize(valor);
        if (onPageSizeChange) onPageSizeChange(valor);
    };

    const handleDiasInativosChange = (e) => {
        const valor = parseInt(e.target.value);
        setDiasInativos(valor);
        if (onDiasInativosChange) onDiasInativosChange(valor);
    };

    const handleLimpar = () => {
        setSearchTerm('');
        setCidadesSelecionadas([]);
        setCidadeSelecionada('');
        setEstadoSelecionado('');
        setCpfBusca('');
        setValorMin('');
        setValorMax('');
        if (onFilterChange) {
            onFilterChange({ cidades: [], cidade: '', estado: '', cpf: '', valorMin: '', valorMax: '', search: '' });
        }
        if (onSearch) {
            onSearch('');
        }
    };

    const temFiltrosAtivos = cidadesSelecionadas.length > 0 || cidadeSelecionada || estadoSelecionado || cpfBusca || valorMin || valorMax || searchTerm;
    const cidadesCount = cidadesDisponiveis.length;
    const estadosCount = estadosDisponiveis.length;

    // Render para modo Gestão de Clientes
    if (isGestaoMode) {
        return (
            <div className={styles.filterBarContainer}>
                <div className={styles.filterGroup}>
                    {/* Cidade - Dropdown Multi-Select */}
                    <div className={styles.filterItem} ref={cidadeDropdownRef} style={{ position: 'relative', minWidth: '180px' }}>
                        <label className={styles.filterLabel}>🏙️ Cidade</label>
                        <div
                            className={`${styles.filterSelect} ${styles.multiSelectTrigger}`}
                            onClick={() => setCidadeDropdownOpen(!cidadeDropdownOpen)}
                            style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {cidadesSelecionadas.length > 0
                                    ? `${cidadesSelecionadas.length} cidade${cidadesSelecionadas.length > 1 ? 's' : ''}`
                                    : 'Todas'
                                }
                            </span>
                            <span style={{ marginLeft: '8px' }}>{cidadeDropdownOpen ? '▲' : '▼'}</span>
                        </div>
                        {cidadeDropdownOpen && (
                            <div className={styles.multiSelectDropdown}>
                                {/* Barra de busca */}
                                <div style={{ padding: '8px', borderBottom: '1px solid #334155' }}>
                                    <input
                                        type="text"
                                        placeholder="Buscar cidade..."
                                        value={cidadeSearch}
                                        onChange={(e) => setCidadeSearch(e.target.value)}
                                        className={styles.cidadeSearchInput}
                                        onClick={(e) => e.stopPropagation()}
                                        autoFocus
                                    />
                                </div>
                                <div className={styles.multiSelectActions}>
                                    <button type="button" onClick={handleLimparCidades} className={styles.multiSelectActionBtn}>
                                        Limpar
                                    </button>
                                    <button type="button" onClick={handleSelecionarTodas} className={styles.multiSelectActionBtn}>
                                        Todas
                                    </button>
                                    <span style={{ color: '#94a3b8', fontSize: '0.75rem', padding: '6px' }}>
                                        {cidadesFiltradas.length} de {cidadesCount}
                                    </span>
                                </div>
                                <div className={styles.multiSelectList}>
                                    {cidadesFiltradas.length > 0 ? cidadesFiltradas.map((cidade) => (
                                        <div
                                            key={cidade}
                                            className={styles.multiSelectItem}
                                            onClick={() => handleCidadeToggle(cidade)}
                                            style={{ background: cidadesSelecionadas.includes(cidade) ? '#334155' : 'transparent' }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={cidadesSelecionadas.includes(cidade)}
                                                readOnly
                                                style={{ marginRight: '8px', accentColor: '#3b82f6' }}
                                            />
                                            <span>{cidade}</span>
                                        </div>
                                    )) : (
                                        <div style={{ padding: '12px', color: '#94a3b8', textAlign: 'center' }}>
                                            Nenhuma cidade encontrada
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Busca */}
                    <div className={styles.filterItem} style={{ minWidth: '200px', flexGrow: 2 }}>
                        <label className={styles.filterLabel}>🔍 Busca</label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.filterInput}
                            placeholder="Nome, CPF/CNPJ, e-mail, telefone..."
                        />
                    </div>
                </div>

                {/* Botões de ação */}
                <div className={styles.actionButtons}>
                    {onExportCSV && (
                        <button
                            onClick={onExportCSV}
                            className={styles.exportButton}
                            title="Exportar todos os clientes para CSV"
                            disabled={isExporting}
                        >
                            {isExporting ? '⏳ Exportando...' : '📥 Exportar'}
                        </button>
                    )}

                    {temFiltrosAtivos && (
                        <button onClick={handleLimpar} className={styles.clearButton} title="Limpar Filtros">
                            <span className={styles.clearIcon}>✕</span>
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Render para modo Legado (Clientes Inativos)
    return (
        <div className={styles.filterBarContainer}>
            <div className={styles.filterGroup}>
                {/* Inativos há */}
                <div className={styles.filterItem} style={{ minWidth: '160px' }}>
                    <label className={styles.filterLabel}>⏱️ Clientes</label>
                    <select
                        value={diasInativos}
                        onChange={handleDiasInativosChange}
                        className={styles.filterSelect}
                    >
                        <option value="0">Todos os clientes</option>
                        <option value="30">1 Mês (30+ dias)</option>
                        <option value="60">2 Meses (60+ dias)</option>
                        <option value="90">3 Meses (90+ dias)</option>
                        <option value="120">4 Meses (120+ dias)</option>
                        <option value="180">6 Meses (180+ dias)</option>
                        <option value="365">1 Ano (365+ dias)</option>
                    </select>
                </div>

                {/* Estado (UF) - Dropdown */}
                <div className={styles.filterItem} ref={estadoDropdownRef} style={{ position: 'relative', minWidth: '120px', maxWidth: '140px' }}>
                    <label className={styles.filterLabel}>🗺️ Estado</label>
                    <div
                        className={`${styles.filterSelect} ${styles.multiSelectTrigger}`}
                        onClick={() => setEstadoDropdownOpen(!estadoDropdownOpen)}
                        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {estadoSelecionado || 'Todos'}
                        </span>
                        <span style={{ marginLeft: '8px' }}>{estadoDropdownOpen ? '▲' : '▼'}</span>
                    </div>
                    {estadoDropdownOpen && (
                        <div className={styles.multiSelectDropdown}>
                            <div className={styles.multiSelectActions}>
                                <button type="button" onClick={() => handleEstadoChange('')} className={styles.multiSelectActionBtn}>
                                    Limpar
                                </button>
                                <span style={{ color: '#94a3b8', fontSize: '0.75rem', padding: '6px' }}>
                                    {estadosCount} estados
                                </span>
                            </div>
                            <div className={styles.multiSelectList}>
                                {estadosDisponiveis.map((uf) => (
                                    <div
                                        key={uf}
                                        className={styles.multiSelectItem}
                                        onClick={() => handleEstadoChange(uf)}
                                        style={{ background: estadoSelecionado === uf ? '#334155' : 'transparent' }}
                                    >
                                        <span>{uf}</span>
                                        {estadoSelecionado === uf && <span>✓</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* CPF/CNPJ - Campo de busca inteligente */}
                <div className={styles.filterItem} style={{ minWidth: '160px', maxWidth: '200px' }}>
                    <label className={styles.filterLabel}>🔎 CPF/CNPJ</label>
                    <input
                        type="text"
                        value={cpfBusca}
                        onChange={handleCpfChange}
                        className={styles.filterInput}
                        placeholder="Digite o CPF..."
                        maxLength={18}
                    />
                </div>

                {/* Valor Mínimo */}
                <div className={styles.filterItem} style={{ minWidth: '100px', maxWidth: '120px' }}>
                    <label className={styles.filterLabel}>💰 Valor Min</label>
                    <div className={styles.valorInputGroup}>
                        <span className={styles.valorPrefix}>R$</span>
                        <input
                            type="number"
                            value={valorMin}
                            onChange={handleValorMinChange}
                            className={styles.filterInputSmall}
                            placeholder="0"
                            min={0}
                        />
                    </div>
                </div>

                {/* Valor Máximo */}
                <div className={styles.filterItem} style={{ minWidth: '100px', maxWidth: '120px' }}>
                    <label className={styles.filterLabel}>💰 Valor Max</label>
                    <div className={styles.valorInputGroup}>
                        <span className={styles.valorPrefix}>R$</span>
                        <input
                            type="number"
                            value={valorMax}
                            onChange={handleValorMaxChange}
                            className={styles.filterInputSmall}
                            placeholder="∞"
                            min={0}
                        />
                    </div>
                </div>

                {/* Ordenar por */}
                <div className={styles.filterItem} style={{ minWidth: '140px' }}>
                    <label className={styles.filterLabel}>📊 Ordenar</label>
                    <select
                        value={ordenacao}
                        onChange={handleOrdenacaoChange}
                        className={styles.filterSelect}
                    >
                        <option value="dias_sem_comprar">Dias sem comprar</option>
                        <option value="ultima_compra">Última compra</option>
                        <option value="total_gasto">Total gasto</option>
                        <option value="contato_nome">Cliente</option>
                    </select>
                </div>

                {/* Por página */}
                <div className={styles.filterItem} style={{ minWidth: '80px', maxWidth: '90px' }}>
                    <label className={styles.filterLabel}>📄 Página</label>
                    <select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        className={styles.filterSelect}
                    >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="200">200</option>
                        <option value="300">300</option>
                        <option value="500">500</option>
                    </select>
                </div>

                {/* Busca */}
                <div className={styles.filterItem} style={{ minWidth: '180px', flexGrow: 2 }}>
                    <label className={styles.filterLabel}>🔍 Busca</label>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                            const val = e.target.value;
                            setSearchTerm(val);
                            // Chamar onSearchChange diretamente com debounce manual
                            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                            searchDebounceRef.current = setTimeout(() => {
                                if (onSearchChange) onSearchChange(val);
                            }, 300);
                        }}
                        className={styles.filterInput}
                        placeholder="Nome, e-mail, telefone..."
                    />
                </div>
            </div>

            {/* Botões de ação */}
            {onAtualizar && (
                <button
                    onClick={onAtualizar}
                    className={styles.updateButton}
                    title="Atualizar dados"
                    disabled={loading}
                >
                    🔄
                </button>
            )}

            {temFiltrosAtivos && (
                <button onClick={handleLimpar} className={styles.clearButton} title="Limpar Filtros">
                    <span className={styles.clearIcon}>✕</span>
                </button>
            )}
        </div>
    );
};

export default FiltrosClientes;
